import { ApolloLink, ApolloClient, HttpLink, InMemoryCache } from '@apollo/client';
import { LocalState } from '@apollo/client/local-state';
import { ErrorLink } from '@apollo/client/link/error';
import { CombinedGraphQLErrors } from '@apollo/client/errors';

import fetchTokenStrategy from 'src/utils/fetch-token-strategy';

import { actionCableLink, hasSubscriptionOperation } from './actioncable-link';
import { suggestChangesVar } from './cache';

const fetchToken = async <TUri extends string, TOptions extends RequestInit>(uri: TUri, options: TOptions) => {
  const token = await fetchTokenStrategy();
  const optionsHeaders = options.headers as Record<string, string>;

  optionsHeaders['Authorization'] = `Bearer ${token}`;

  const impersonatedUserID = localStorage.getItem('impersonatedUserID');

  if (impersonatedUserID) {
    optionsHeaders['X-Impersonated-User-ID'] = impersonatedUserID;
  }

  return fetch(uri, options);
};

export const apolloCache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        clientChangesRequested: {
          read() {
            return suggestChangesVar();
          },
        },
        company: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        billing: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
      },
    },
    SuggestChangePage: {
      merge: true,
    },
    Backup: {
      fields: {
        backupAssets: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Depositor: {
      fields: {
        contacts: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    Beneficiary: {
      fields: {
        contacts: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    AssetReportGroup: {
      fields: {
        assetReports: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    BackupAssetReportGroup: {
      fields: {
        backupAssetReports: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    BackupPolicy: {
      merge: true,
    },
    AppPolicy: {
      merge: true,
    },
    CustodianProfile: {
      fields: {
        policy: {
          merge(_, incoming) {
            return incoming;
          },
        },
        custodianOperations: {
          merge(_, incoming) {
            return incoming;
          },
        },
      },
    },
    SoftwareEscrowPolicy: {
      merge: true,
    },
    AgreementPolicy: {
      merge: true,
    },
    ContinuityEscrowPolicy: {
      merge: true,
    },
    EscrowInvitationPolicy: {
      merge: true,
    },
    EscrowTermination: {
      merge: true,
    },
    UserPolicy: {
      merge: true,
    },
    IntegrationPolicy: {
      merge: true,
    },
  },
});

const httpLink = new HttpLink({
  uri: `${import.meta.env.VITE_BACKEND_URL}/graphql`,
  fetch: fetchToken,
});

const splitLink = ApolloLink.split(hasSubscriptionOperation, actionCableLink, httpLink);

export const apolloClient = new ApolloClient({
  cache: apolloCache,

  link: ApolloLink.from([
    new ErrorLink(({ error }) => {
      if (CombinedGraphQLErrors.is(error)) {
        error.errors.forEach(({ message, locations, path }) =>
          console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`),
        );
      } else {
        console.log(`[Network error]: ${error}`);
      }
    }),
    splitLink,
  ]),

  localState: new LocalState({
    resolvers: {},
  }),
});

const writeInitialData = async () => {};

writeInitialData();

apolloClient.onResetStore(writeInitialData);
